package top.wcpe.allincore.ui.runtime.render.model

/**
 * 渲染命令类型枚举，描述一次绘制意图的具体类型。
 *
 * ADR-0072 决策 1 镜像：真源为 `platform-client-api` 的
 * `top.wcpe.allincore.platform.client.api.render.command.RenderCommandType`
 * （纯 Java 8 对外 API，按 ADR-0002 不得 KMP 化）。值名与声明序逐一照抄真源
 * （ordinal 序即接缝约定），一致性由 jvmTest 的 RenderModelConsistencyTest 守护。
 */
enum class RenderCommandType {
    /** 填充矩形 */
    FILL_RECT,

    /** 绘制文本 */
    DRAW_TEXT,

    /** 绘制内联富文本（[kind:id] 图标标签 + 图文混排 + § 颜色码） */
    DRAW_RICH_TEXT,

    /** 绘制线段 */
    DRAW_LINE,

    /** 绘制贴图 */
    DRAW_IMAGE,

    /** 带样式的圆角矩形（SDF 圆角 + 边框） */
    ROUNDED_RECT,

    /** 渐变填充 */
    GRADIENT,

    /** 阴影绘制 */
    SHADOW,

    /** 带纹理变换样式的贴图 */
    IMAGE_STYLED,

    /** 绘制 Minecraft 物品图标（含数量徽标） */
    DRAW_ITEM_STACK,

    /** 绘制贴图子区域（uv 切片） */
    DRAW_TEXTURED_QUAD,

    /** 绘制 2D 实体预览 */
    DRAW_ENTITY_PREVIEW,

    /** 霓虹辉光特效 */
    NEON_GLOW,

    /** 涟漪特效 */
    RIPPLE,

    /** 程序化纹理特效 */
    PROCEDURAL_TEXTURE,

    /** 程序化材质层 */
    MATERIAL_LAYER,

    /** 流动光效 */
    FLOW,

    /** 能量脉冲特效 */
    ENERGY,

    /**
     * filter 组（CSS filter: blur 高斯模糊）：载荷为「begin..end 之间捕获的子命令列表 + 矩形 + 模糊半径」，
     * 层缓冲回放时先 beginFilterGroup（重定向到离屏 FBO）→ 逐条回放子命令 → endFilterGroup（两 pass 高斯合成回主屏）。
     */
    FILTER_GROUP,

    /**
     * backdrop-filter 背景模糊（毛玻璃）：载荷为「元素矩形 + 模糊半径」，层缓冲回放时先 flush
     * （保证更早层已落主帧缓冲）→ applyBackdropBlur（就地 blit 背景 → 高斯 → 画回元素 rect）。
     */
    BACKDROP_FILTER,

    /**
     * 混合组（CSS mix-blend-mode）：载荷为「push..pop 之间捕获的子命令列表 + 混合模式序数」，
     * 层缓冲回放时先 pushBlendMode（切 GL 混合函数）→ 逐条回放子命令 → popBlendMode（还原默认混合函数）。
     */
    BLEND_GROUP,

    /**
     * 历史组（FR-UI-023 historyLayer 局部低清跨帧历史采样）：载荷为「begin..end 之间捕获的子命令列表 +
     * 矩形 + 元素 id + 历史层参数」，层缓冲回放时先 beginHistoryCapture（source=self 时重定向元素绘制到离屏隔离靶）
     * → 逐条回放子命令 → endHistoryCapture（捕获元素自身像素入环形缓冲、叠加历史余晖后合成回主屏）。
     */
    HISTORY_GROUP,

    /**
     * 粒子层（FR-UI-031 UI 粒子系统与高级动效）：单命令（非 group），载荷为「元素矩形 + 元素 id + 层序号 +
     * 粒子参数快照（含随帧变的鼠标坐标与交互态标志）」，层缓冲回放时调 drawParticleLayer——平台按 (elementId, ordinal)
     * 驱动中立粒子模拟推进物理并 GPU 实例化绘制快照；不支持 / 超预算 / shader 失败时优雅降级跳过。
     */
    PARTICLE,

    /** 绘制独立模型预览；仅追加在尾部以冻结既有 ordinal。 */
    DRAW_MODEL_PREVIEW,
}
